import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { parseMatchTime } from "../../utils/dateUtils";

const API_URL = getBaseURL();

const ManageMatchPlayers = () => {
    const [matches, setMatches] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState("");
    const [players, setPlayers] = useState([]);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [formData, setFormData] = useState({ player_name: "", role: "", credit: 0, image: "" });

    const token = localStorage.getItem("adminToken");
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) navigate("/winrox11-super-admin");
        else fetchMatches();
    }, [token, navigate]);

    const fetchMatches = async () => {
        try {
            const res = await axios.get(`${API_URL}/admin/matches/live-upcoming`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMatches(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchPlayers = async (matchId) => {
        if (!matchId) return;
        try {
            const res = await axios.get(`${API_URL}/admin/players/${matchId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPlayers(res.data);
        } catch (err) { console.error(err); }
    };

    const handleMatchChange = (e) => {
        const id = e.target.value;
        setSelectedMatch(id);
        fetchPlayers(id);
        setEditingPlayer(null);
    };

    const handleEdit = (p) => {
        setEditingPlayer(p.player_id);
        setFormData({ player_name: p.player_name, role: p.role, credit: p.credit, image: p.image || "" });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API_URL}/admin/players/match/${editingPlayer}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Match player updated! This will now show correctly on the frontend app.");
            setEditingPlayer(null);
            fetchPlayers(selectedMatch);
        } catch (err) {
            alert("Update failed");
        }
    };

    const handleSync = async () => {
        if(!selectedMatch) return;
        if(!window.confirm("This will re-fetch the entire squad from Sportmonks and RESET all player roles/credits for this match. Continue?")) return;
        
        try {
            alert("Syncing started... please wait.");
            const res = await axios.post(`${API_URL}/admin/players/sync/${selectedMatch}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(res.data.msg);
            fetchPlayers(selectedMatch);
        } catch (err) {
            alert("Sync failed: " + (err.response?.data?.message || "Check API Key or Match ID"));
        }
    };

    return (
        <div className="admin-body" style={{ padding: "30px", fontFamily: "sans-serif", maxWidth: "1000px", margin: "0 auto" }}>
            <Link to="/winrox11-super-admin/dashboard" style={{ color: "blue", textDecoration: "none" }}>← Back to Dashboard</Link>
            <h2>⚙️ Manage Match-Specific Players</h2>
            <p style={{ color: "gray" }}>Use this to fix typos or change credits for players ALREADY in a match. (Master database changes don't affect existing matches)</p>

            <div style={{ marginBottom: "20px" }}>
                <label style={{ fontWeight: "bold" }}>Select Match: </label>
                <select value={selectedMatch} onChange={handleMatchChange} style={{ padding: "10px", width: "100%", marginTop: "5px" }}>
                    <option value="">-- Choose a Match --</option>
                    {matches.map(m => (
                        <option key={m.match_id} value={m.match_id}>
                            [{m.format}] {m.team1} vs {m.team2} - {parseMatchTime(m).toLocaleDateString()}
                        </option>
                    ))}
                </select>

                {selectedMatch && (
                    <div style={{ marginTop: "10px", textAlign: "right" }}>
                        <button 
                            onClick={handleSync} 
                            style={{ padding: "10px 20px", background: "#f6ad55", color: "black", border: "none", borderRadius: "5px", fontWeight: "bold", cursor: "pointer" }}
                        >
                            🔄 Re-Sync Squad from Sportmonks (Fixed Logic)
                        </button>
                    </div>
                )}
            </div>

            {editingPlayer && (
                <div style={{ background: "#fffaf0", padding: "20px", borderRadius: "10px", border: "1px solid #ed8936", marginBottom: "30px" }}>
                    <h3>✏️ Edit Player in this Match</h3>
                    <form onSubmit={handleUpdate} style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: 1 }}>
                            <label>Name:</label>
                            <input type="text" value={formData.player_name} onChange={e => setFormData({...formData, player_name: e.target.value})} required style={{ padding: "10px", width: "100%" }}/>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Role:</label>
                            <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} style={{ padding: "10px", width: "100%" }}>
                                <option value="BAT">BAT</option>
                                <option value="BOWL">BOWL</option>
                                <option value="AR">AR</option>
                                <option value="WK">WK</option>
                            </select>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Credit:</label>
                            <input type="number" step="0.5" value={formData.credit} onChange={e => setFormData({...formData, credit: e.target.value})} required style={{ padding: "10px", width: "100%" }}/>
                        </div>
                        <button type="submit" style={{ padding: "12px 20px", background: "#ed8936", color: "white", border: "none", borderRadius: "5px", fontWeight: "bold" }}>Update for this Match</button>
                        <button type="button" onClick={() => setEditingPlayer(null)} style={{ padding: "12px 20px", background: "gray", color: "white", border: "none", borderRadius: "5px" }}>Cancel</button>
                    </form>
                </div>
            )}

            {selectedMatch && (
                <div style={{ overflowX: "auto", background: "white", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ background: "#edf2f7" }}>
                            <tr>
                                <th style={{ padding: "10px", border: "1px solid #ddd" }}>Team</th>
                                <th style={{ padding: "10px", border: "1px solid #ddd" }}>Avatar</th>
                                <th style={{ padding: "10px", border: "1px solid #ddd" }}>Name</th>
                                <th style={{ padding: "10px", border: "1px solid #ddd" }}>Role</th>
                                <th style={{ padding: "10px", border: "1px solid #ddd" }}>Credit</th>
                                <th style={{ padding: "10px", border: "1px solid #ddd" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.map(p => (
                                <tr key={p.player_id}>
                                    <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center", fontWeight: "bold" }}>{p.team}</td>
                                    <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center" }}>
                                        <img src={p.image} alt="" style={{ width: "30px", height: "30px", borderRadius: "50%" }} />
                                    </td>
                                    <td style={{ padding: "10px", border: "1px solid #ddd" }}>{p.player_name}</td>
                                    <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center" }}>{p.role}</td>
                                    <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center" }}>{p.credit}</td>
                                    <td style={{ padding: "10px", border: "1px solid #ddd", textAlign: "center" }}>
                                        <button onClick={() => handleEdit(p)} style={{ background: "#3182ce", color: "white", border: "none", padding: "5px 10px", borderRadius: "3px" }}>Edit Data</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ManageMatchPlayers;
